import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import type { DateRange } from '@mui/x-date-pickers-pro';
import { LocalizationProvider, DateRangePicker as MuiDateRangePicker } from '@mui/x-date-pickers-pro';
import is from '@sindresorhus/is';
import { format, isValid } from 'date-fns';
import type { FunctionComponent } from 'react';
import { useContext } from 'react';

import { ApplicationContext } from '~/shared/context/context';

type DateRangePickerProps = {
    readonly label: string;
    readonly labelEnd: string;
    readonly labelStart: string;
    readonly onChange: (value: DateRange<Date>) => void;
    readonly value?: DateRange<Date>;
};

export const DateRangePicker: FunctionComponent<DateRangePickerProps> = ({
    label,
    labelEnd,
    labelStart,
    onChange,
    value,
}) => {
    const {
        applicationState: { language },
        translate,
    } = useContext(ApplicationContext);

    const handleChange = ([start, end]: DateRange<Date>): void => {
        const startDate = is.null(start) === false && isValid(start) ? new Date(format(start, 'yyyy-MM-dd')) : null;
        const endDate = is.null(end) === false && isValid(end) ? new Date(format(end, 'yyyy-MM-dd')) : null;
        onChange([startDate, endDate]);
    };

    return (
        <Grid container={true} flexDirection="column" gap={1}>
            <Typography variant="body1">{label}</Typography>
            <LocalizationProvider
                adapterLocale={language.date.locale}
                dateAdapter={AdapterDateFns}
                localeText={{
                    clearButtonLabel: translate('components.form-date-picker-field.clear') ?? '',
                    end: labelEnd,
                    nextMonth: translate('components.form-date-picker-field.next-month') ?? '',
                    previousMonth: translate('components.form-date-picker-field.previous-month') ?? '',
                    start: labelStart,
                }}
            >
                <MuiDateRangePicker
                    calendars={1}
                    slotProps={{
                        textField: {
                            placeholder: language.date.placeholder,
                        },
                    }}
                    value={is.undefined(value) ? [null, null] : value}
                    onChange={handleChange}
                />
            </LocalizationProvider>
        </Grid>
    );
};
